"use client";

import React, { useState } from "react";
import { Button } from "../common/button";

export interface TherapistTiersClearProps {
  title?: string;
  subtitle?: string;
  onCtaClick?: () => void;
  onMeetTherapistsClick?: () => void;
  showIntern?: boolean;
  showAssociate?: boolean;
  partnershipName?: string;
  sessionsCovered?: number | null;
}

export function TherapistTiersClear({
  title = "Which therapist is right for you?",
  subtitle = "Quick question: Is budget your main concern, or do you need specialized expertise?",
  onCtaClick,
  onMeetTherapistsClick,
  showIntern = true,
  showAssociate = true,
  partnershipName,
  sessionsCovered
}: TherapistTiersClearProps) {
  const [showScopes, setShowScopes] = useState(false);

  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {partnershipName && sessionsCovered ? (() => {
              const coveredTypes = [];
              if (showIntern) coveredTypes.push('Intern');
              if (showAssociate) coveredTypes.push('Associate');

              let coverageText = '';
              if (coveredTypes.length === 2) {
                coverageText = 'with either therapist type';
              } else if (coveredTypes.length === 1) {
                coverageText = `with ${coveredTypes[0]} therapists only`;
              }

              return `${partnershipName} covers your first ${sessionsCovered} sessions ${coverageText}. The rates shown below are for if you choose to continue after your covered sessions.`;
            })() : subtitle}
          </p>
        </div>

        {/* Tier comparison cards */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Intern Card */}
          <div
            className={`bg-white rounded-2xl border-2 transition-all ${
              showIntern
                ? 'border-green-500 hover:border-green-600'
                : 'border-gray-200 hover:border-gray-300 opacity-75'
            }`}
          >
            {/* Coverage Badge */}
            {partnershipName && showIntern && (
              <div className="bg-green-500 text-white text-sm font-medium px-4 py-2 rounded-t-xl text-center">
                ✓ Covered by {partnershipName}
              </div>
            )}
            {partnershipName && !showIntern && (
              <div className="bg-gray-100 text-gray-600 text-sm font-medium px-4 py-2 rounded-t-xl text-center">
                Not included in {partnershipName} program
              </div>
            )}

            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="mb-2">
                <h3 className="text-2xl font-bold text-gray-900">Intern Therapists</h3>
              </div>
              <div className="mb-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  {partnershipName && sessionsCovered ? 'After covered sessions - Standard Rate' : 'Standard Rate'}
                </p>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold text-gray-900">$115</span>
                  <span className="text-sm text-gray-600">per session</span>
                </div>
                <div className="bg-green-50 inline-flex items-center px-3 py-1 rounded-full">
                  <span className="text-sm font-medium text-green-700">Pay what you can: $45-115</span>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                Talk to someone who gets you. Master's-level therapists with fresh training and genuine passion for helping you feel better.
              </p>

              {/* Who they are */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Why clients love them:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">Double the expertise: Your therapist + their licensed supervisor working on your care</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">Currently training in the latest evidence-based approaches that actually work</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">Lower caseloads mean more energy and attention for you (10-15 vs 30+ clients)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">Understand modern challenges - from social media stress to remote work burnout</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">Many bring wisdom from previous careers - teachers, nurses, engineers turned therapists</span>
                  </li>
                </ul>
              </div>

              {/* Scope note */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-600">
                  <span className="font-medium">Scope limitations:</span> Cannot work with severe mental illness, active addiction, recent suicide attempts, eating disorders, trauma therapy, or court-mandated therapy
                </p>
              </div>
            </div>
          </div>

          {/* Associate Card */}
          <div
            className={`bg-white rounded-2xl border-2 transition-all ${
              showAssociate
                ? 'border-green-500 hover:border-green-600'
                : 'border-gray-200 hover:border-gray-300 opacity-75'
            }`}
          >
            {/* Coverage Badge */}
            {partnershipName && showAssociate && (
              <div className="bg-green-500 text-white text-sm font-medium px-4 py-2 rounded-t-xl text-center">
                ✓ Covered by {partnershipName}
              </div>
            )}
            {partnershipName && !showAssociate && (
              <div className="bg-gray-100 text-gray-600 text-sm font-medium px-4 py-2 rounded-t-xl text-center">
                Not included in {partnershipName} program
              </div>
            )}

            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="mb-2">
                <h3 className="text-2xl font-bold text-gray-900">Associate Therapists</h3>
              </div>
              <div className="mb-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  {partnershipName && sessionsCovered ? 'After covered sessions - Standard Rate' : 'Standard Rate'}
                </p>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold text-gray-900">$200</span>
                  <span className="text-sm text-gray-600">per session</span>
                </div>
                <div className="bg-blue-50 inline-flex items-center px-3 py-1 rounded-full">
                  <span className="text-sm font-medium text-blue-700">Pay what you can: $125-200</span>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                If budget isn't your main concern, these experienced therapists offer the confidence that comes with completed training and broader expertise.
              </p>

              {/* Who they are */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Why choose Associate:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">Fully completed their master's degree and all practicum requirements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">More clinical hours means they've seen more situations similar to yours</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">Great for everything - from daily stress to complex mental health needs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">Can work with trauma, addiction, court-mandated therapy, and specialized needs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">Many have additional training in specialized approaches like EMDR, CBT, or DBT</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Decision helper */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Still not sure?</h3>
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              <div className="pb-4 md:pb-0 border-b md:border-0 border-gray-200">
                <p className="font-medium text-gray-900 mb-1">Choose Intern if:</p>
                <p className="text-gray-600">You want fresh energy, cost savings, and excellent care</p>
              </div>
              <div className="pb-4 md:pb-0 border-b md:border-0 md:border-x border-gray-200 md:px-6">
                <p className="font-medium text-gray-900 mb-1">Both are great for:</p>
                <p className="text-gray-600">Anxiety, depression, relationships, stress, and most therapy needs</p>
              </div>
              <div>
                <p className="font-medium text-gray-900 mb-1">Choose Associate if:</p>
                <p className="text-gray-600">You prefer extensive experience or need specialized care</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg"
              className="bg-primary-600 hover:bg-primary-700"
              onClick={onCtaClick}
            >
              Take the Matching Quiz
            </Button>
            <button
              onClick={onMeetTherapistsClick}
              className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              Meet the therapists →
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-3">
            2-minute quiz • Get matched with your perfect therapist
          </p>
        </div>
      </div>
    </section>
  );
}